import {
  CalendarDefinitionSchema,
  asCalendar,
  toCanonicalDay,
  type Calendar,
  type CalendarDefinition,
  type Era,
  type EraDirection,
  type LeapRule,
  type MonthDefinition,
} from '@dl/calendar';
import { button, div, input, span, text, type ElProps, type Mountable } from '@llui/dom';

import { calendarDatePicker } from './calendar-date-picker.js';

/**
 * `<CalendarEditor>` — authors a fictional-calendar `CalendarDefinition`
 * with a LIVE PREVIEW embedding the reader-facing `calendarDatePicker`.
 *
 * Faithful to the design mock `mocks/Calendar Editor (standalone)`. The
 * mock specs SIX surfaces — a list-first manager, a New/empty state, a
 * Simple-rename journey, the Advanced full-model form, inline Validation,
 * and Fork-on-edit. This atom renders the load-bearing two:
 *
 *  - **list mode** (`mode: 'list'`) — the campaign's calendars listed
 *    (name, structure summary, a "primary" badge, Edit / Fork / Inspect),
 *    with "Browse packs" + "New calendar" actions, an empty-state, and
 *    the single-primary advisory. Bundled / pack calendars are read-only
 *    → **Fork** to edit. The list is the entry point — never the editor.
 *
 *  - **editor mode** (`mode: 'simple' | 'advanced'`) — the section editor.
 *    `simple` is the 95% rename path (name + era + month-rename, lengths
 *    locked, an "Advanced…" disclosure). `advanced` exposes the full
 *    model: identity, months (name / length / leap_length / intercalary),
 *    leap rule (none | every-N | julian | gregorian), eras (name / abbrev
 *    / start_day / year_one / direction), an optional weekday cycle (+
 *    phase), optional sub-day periods, and the anchor (canonical day +
 *    named event). Beside the form sits the live preview.
 *
 * ## Stateless atom — state lives in the HOST
 *
 * `@dl/ui` atoms are stateless and rendered DECLARATIVELY (no
 * `document.createElement`, no imperative body-build in `onMount`). This
 * editor is a pure function of its props: it takes the in-progress
 * `WorkingCalendar` (an editable draft whose numeric fields are strings so
 * they can be empty / invalid mid-keystroke), the open advanced section,
 * the fork status, the resolved validation issues, and emits the GM's
 * intent through `on…` callbacks (`onName`, `onMonth`, `onAddMonth`,
 * `onLeapRule`, `onEra`, …). The HOST owns the working draft in its Model
 * and re-renders on change — so editing a month length re-renders the
 * embedded preview instantly (the "editor = reader" invariant).
 *
 * The preview consumes a real `@dl/calendar` `Calendar`: the editor folds
 * the working draft to a `CalendarDefinition`, runs it through
 * `asCalendar()`, and feeds the picker. When the draft is structurally
 * invalid (a 0-length month, an out-of-order era) the picker falls back to
 * the last valid projection so the preview never goes blank — validation
 * surfaces the error inline instead.
 *
 * ## Validation
 *
 * Validation MIRRORS what `asCalendar()` / `CalendarDefinitionSchema`
 * enforce — month length > 0, eras ordered by `start_day`, a resolvable
 * anchor. `validateWorkingCalendar(working)` is exported so the host can
 * compute issues in its reducer; the editor renders them inline (field-red
 * + reason) and the host gates Save on the blocking ones.
 *
 * ## A11y
 *
 * Real `<input>` / `<select>` / `<button>` controls throughout, each with
 * a `<label>` or `aria-label`. Color is never the only signal — an invalid
 * field pairs the red chrome with `aria-invalid` + a textual reason and a
 * ⚠ glyph; the primary badge pairs the accent border with a "Primary"
 * text label. All chrome copy flows through the `messages` catalog; the
 * calendar field VALUES (month / era names) are user data and stay as
 * typed. RTL-safe (logical properties in the CSS).
 */

// ---------- working (editable) draft ----------
//
// The editor edits a DRAFT, not a finished `CalendarDefinition`: numeric
// fields are strings (so an input can be empty / mid-typing), and the
// opaque `id` carries through untouched. The host folds the draft to a
// real `CalendarDefinition` on Save.

/** A month row, as edited. `length` / `leapLength` are raw strings. */
export interface WorkingMonth {
  name: string;
  /** Days in a common year, as a raw input string. */
  length: string;
  /** Days in a leap year (optional), as a raw input string. */
  leapLength?: string;
  /** Festival day outside the normal month sequence. */
  intercalary?: boolean;
}

/** An era row, as edited. `startDay` / `yearOne` are raw strings. */
export interface WorkingEra {
  id: string;
  name: string;
  abbreviation?: string;
  /** Canonical day this era begins, as a raw string. Empty = leading (`null`). */
  startDay: string;
  yearOne: string;
  direction: EraDirection;
}

/** A weekday label, as edited (the stable name; localized label is host data). */
export interface WorkingWeekday {
  name: string;
}

/** A sub-day period, as edited. */
export interface WorkingPeriod {
  name: string;
}

/**
 * The in-progress calendar the host owns and the editor edits. A superset
 * of `CalendarDefinition` with numeric fields widened to strings.
 */
export interface WorkingCalendar {
  /** Opaque, immutable `cal_<uuid>` id — carried through, never edited. */
  id: string;
  /** Display name (default-locale string the GM types). */
  name: string;
  months: WorkingMonth[];
  leapRule: LeapRule;
  eras: WorkingEra[];
  /** Optional weekday cycle. `undefined` → a weekless calendar. */
  weekdays?: { labels: WorkingWeekday[]; phase: string };
  /** Optional named sub-day periods. */
  subDayPeriods?: WorkingPeriod[];
  /** Canonical day the origin sits on, as a raw string. */
  anchorCanonicalDay: string;
  /** Optional named in-world origin event (e.g. "The Founding"). */
  anchorName?: string;
}

// ---------- validation ----------

export type CalendarIssueSeverity = 'error' | 'warning';

/** A single validation issue, keyed for i18n (the editor never renders raw
 * English from the engine — the host maps `messageKey` through `t()`). */
export interface CalendarIssue {
  /** `error` blocks Save; `warning` advises only. */
  severity: CalendarIssueSeverity;
  /** i18n message key (resolved by the host / the editor `messages`). */
  messageKey: string;
  /** Interpolation params for the message. */
  params?: Record<string, string | number>;
  /** The field the issue attaches to, for inline highlighting. */
  field:
    | { kind: 'month'; index: number }
    | { kind: 'era'; index: number }
    | { kind: 'anchor' }
    | { kind: 'name' };
}

/** Parse a raw input string to an integer, or `null` if blank / NaN. */
function parseIntOrNull(s: string): number | null {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isInteger(n) ? n : null;
}

/**
 * Fold the working draft to a `CalendarDefinition`, or return `null` if a
 * numeric field can't parse (so the caller can fall back to a last-valid
 * projection). The fold mirrors `asCalendar()`'s field shape.
 */
export function workingToDefinition(working: WorkingCalendar): CalendarDefinition | null {
  const months: MonthDefinition[] = [];
  for (const m of working.months) {
    const length = parseIntOrNull(m.length);
    if (length === null || length <= 0) return null;
    const month: MonthDefinition = { name: m.name.trim() === '' ? 'month' : m.name, length };
    if (m.leapLength !== undefined && m.leapLength.trim() !== '') {
      const ll = parseIntOrNull(m.leapLength);
      if (ll === null || ll <= 0) return null;
      month.leap_length = ll;
    }
    if (m.intercalary === true) month.intercalary = true;
    months.push(month);
  }
  if (months.length === 0) return null;

  const eras: Era[] = [];
  for (const e of working.eras) {
    const yearOne = parseIntOrNull(e.yearOne);
    if (yearOne === null) return null;
    const startRaw = e.startDay.trim();
    const startDay = startRaw === '' ? null : parseIntOrNull(e.startDay);
    if (startRaw !== '' && startDay === null) return null;
    const era: Era = {
      id: e.id,
      name: e.name.trim() === '' ? 'era' : e.name,
      start_day: startDay,
      year_one: yearOne,
      direction: e.direction,
    };
    if (e.abbreviation !== undefined && e.abbreviation.trim() !== '') {
      era.abbreviation = e.abbreviation.trim();
    }
    eras.push(era);
  }
  if (eras.length === 0) return null;

  const anchorDay = parseIntOrNull(working.anchorCanonicalDay);
  if (anchorDay === null) return null;

  const def: CalendarDefinition = {
    id: working.id,
    name: working.name.trim() === '' ? 'Calendar' : working.name,
    cycles: {
      year: { months },
      ...(working.weekdays !== undefined && working.weekdays.labels.length > 0
        ? {
            week: {
              labels: working.weekdays.labels.map((w) => w.name),
              phase: Math.max(0, parseIntOrNull(working.weekdays.phase) ?? 0),
            },
          }
        : {}),
    },
    eras,
    leap_rule: working.leapRule,
    anchor_canonical_day: anchorDay,
  };
  if (working.anchorName !== undefined && working.anchorName.trim() !== '') {
    def.anchor = { name: working.anchorName.trim() };
  }
  return def;
}

/**
 * Validate the working draft, mirroring `asCalendar()` /
 * `CalendarDefinitionSchema`: month length > 0, eras ordered by
 * `start_day`, a resolvable anchor, and an unnamed-anchor warning. Returns
 * keyed issues; the host gates Save on `severity === 'error'`.
 */
export function validateWorkingCalendar(working: WorkingCalendar): CalendarIssue[] {
  const issues: CalendarIssue[] = [];

  if (working.name.trim() === '') {
    issues.push({ severity: 'error', messageKey: 'calendar_editor.error.name_required', field: { kind: 'name' } });
  }

  // Months — each length must parse to a positive integer.
  working.months.forEach((m, index) => {
    const length = parseIntOrNull(m.length);
    if (length === null || length <= 0) {
      issues.push({
        severity: 'error',
        messageKey: 'calendar_editor.error.month_length',
        params: { name: m.name },
        field: { kind: 'month', index },
      });
    }
    if (m.leapLength !== undefined && m.leapLength.trim() !== '') {
      const ll = parseIntOrNull(m.leapLength);
      if (ll === null || ll <= 0) {
        issues.push({
          severity: 'error',
          messageKey: 'calendar_editor.error.month_leap_length',
          params: { name: m.name },
          field: { kind: 'month', index },
        });
      }
    }
  });

  // Eras — non-leading start_days must be strictly increasing.
  let prevStart: number | null = null;
  working.eras.forEach((e, index) => {
    const yearOne = parseIntOrNull(e.yearOne);
    if (yearOne === null) {
      issues.push({
        severity: 'error',
        messageKey: 'calendar_editor.error.era_year_one',
        params: { name: e.name },
        field: { kind: 'era', index },
      });
    }
    const startRaw = e.startDay.trim();
    if (startRaw !== '') {
      const start = parseIntOrNull(e.startDay);
      if (start === null) {
        issues.push({
          severity: 'error',
          messageKey: 'calendar_editor.error.era_start_day',
          params: { name: e.name },
          field: { kind: 'era', index },
        });
      } else {
        if (prevStart !== null && start <= prevStart) {
          issues.push({
            severity: 'error',
            messageKey: 'calendar_editor.error.era_order',
            params: { name: e.name },
            field: { kind: 'era', index },
          });
        }
        prevStart = start;
      }
    }
  });

  // Anchor — must parse; if a valid definition folds, probe the origin.
  const anchorDay = parseIntOrNull(working.anchorCanonicalDay);
  if (anchorDay === null) {
    issues.push({
      severity: 'error',
      messageKey: 'calendar_editor.error.anchor_day',
      field: { kind: 'anchor' },
    });
  }
  if (working.anchorName === undefined || working.anchorName.trim() === '') {
    issues.push({
      severity: 'warning',
      messageKey: 'calendar_editor.warn.anchor_unnamed',
      field: { kind: 'anchor' },
    });
  }

  // Final structural pass — if the draft folds, run the real schema +
  // engine so the editor's validation can never diverge from the chokepoint.
  const def = workingToDefinition(working);
  if (def !== null) {
    const parsed = CalendarDefinitionSchema.safeParse(def);
    if (!parsed.success) {
      issues.push({
        severity: 'error',
        messageKey: 'calendar_editor.error.schema',
        field: { kind: 'name' },
      });
    } else {
      // Probe the origin display date round-trips (the bijection holds).
      const calendar = asCalendar(parsed.data);
      const e0 = calendar.eras[0];
      if (e0 !== undefined) {
        const origin = toCanonicalDay(calendar, {
          era: e0.id,
          year: e0.year_one,
          month: 1,
          day: 1,
        });
        if (!origin.ok) {
          issues.push({
            severity: 'error',
            messageKey: 'calendar_editor.error.anchor_unresolvable',
            field: { kind: 'anchor' },
          });
        }
      }
    }
  }

  return issues;
}

// ---------- list-mode entries ----------

/** Origin / editability of a listed calendar. */
export type CalendarListSource = 'campaign' | 'bundled' | 'plugin';

/** One row in the list-first manager. */
export interface CalendarListEntry {
  /** Stable id (the `cal_` id or a pack token). */
  id: string;
  /** Display name. */
  name: string;
  /** A short structure summary (e.g. "12 months · 7-day week · leap"). */
  summary: string;
  /** Last-edited caption (host-formatted; optional). */
  edited?: string;
  /** Whether this is the campaign's single primary time axis. */
  primary?: boolean;
  /** Origin — drives the row action: campaign → Edit, bundled → Fork,
   * plugin → Inspect (read-only). */
  source: CalendarListSource;
}

// ---------- messages ----------

/** Catalog of localizable chrome strings. The host overrides these to
 * integrate its own `t()`; calendar field VALUES are user data, never
 * routed through here. */
export interface CalendarEditorMessages {
  // list mode
  listEyebrow: string;
  listTitle: string;
  browsePacks: string;
  newCalendar: string;
  edit: string;
  fork: string;
  inspect: string;
  primary: string;
  bundled: string;
  pluginPreview: string;
  singlePrimaryTitle: string;
  singlePrimaryBody: string;
  emptyTitle: string;
  emptyBody: string;
  // editor chrome
  backToSimple: string;
  advanced: string;
  cancel: string;
  save: string;
  done: string;
  forkAndEdit: string;
  readOnlyBar: string;
  forkTitle: string;
  forkBody: string;
  // sections
  identity: string;
  calendarName: string;
  months: string;
  monthsHint: string;
  renameOnly: string;
  addMonth: string;
  markIntercalary: string;
  remove: string;
  festival: string;
  leapRule: string;
  leapNone: string;
  leapEveryN: string;
  leapJulian: string;
  leapGregorian: string;
  leapEvery: string;
  leapPhase: string;
  leapHint: string;
  eras: string;
  erasHint: string;
  eraName: string;
  abbreviation: string;
  startDay: string;
  startDayHelp: string;
  yearOne: string;
  yearOneHelp: string;
  direction: string;
  forward: string;
  backward: string;
  addEra: string;
  weekdayCycle: string;
  weekdayHint: string;
  phase: string;
  phaseHelp: string;
  addWeekday: string;
  subDay: string;
  subDayHint: string;
  addPeriod: string;
  period: string;
  anchor: string;
  anchorHint: string;
  anchorDay: string;
  anchorEvent: string;
  // preview
  livePreview: string;
  updatesAsYouEdit: string;
  readsToPlayers: string;
  // misc
  optional: string;
  required: string;
  lengthLocked: string;
  days: string;
  errorsBlock: string;
  noErrors: string;
}

export const DEFAULT_CALENDAR_EDITOR_MESSAGES: CalendarEditorMessages = {
  listEyebrow: 'Calendars',
  listTitle: 'Calendars',
  browsePacks: 'Browse packs',
  newCalendar: 'New calendar',
  edit: 'Edit',
  fork: 'Fork',
  inspect: 'Inspect',
  primary: 'Primary',
  bundled: 'Bundled',
  pluginPreview: 'Plugin · preview',
  singlePrimaryTitle: "One calendar is the campaign's primary time axis",
  singlePrimaryBody:
    'All in-world dates resolve against the primary. Others stay available for conversion but never drive the session cursor.',
  emptyTitle: 'No calendars yet',
  emptyBody:
    'Most worlds start from a familiar shape and just rename it. Pick a starting point — you can rename months, eras, and the year without touching the structure.',
  backToSimple: 'Back to simple',
  advanced: 'Advanced / Custom calendar…',
  cancel: 'Cancel',
  save: 'Save calendar',
  done: 'Done',
  forkAndEdit: 'Fork & edit',
  readOnlyBar: 'Bundled calendars are shared across every campaign.',
  forkTitle: 'Editing forks a campaign copy',
  forkBody: "Your changes won't touch the shared pack — they'll live in this campaign.",
  identity: 'Identity',
  calendarName: 'Calendar name',
  months: 'Months',
  monthsHint:
    'Ordered list. Edit a length inline, or mark a row intercalary — a festival day outside the month sequence.',
  renameOnly: 'rename only',
  addMonth: 'Add month',
  markIntercalary: 'Mark intercalary',
  remove: 'Remove',
  festival: 'Festival',
  leapRule: 'Leap rule',
  leapNone: 'None',
  leapEveryN: 'Every N',
  leapJulian: 'Julian',
  leapGregorian: 'Gregorian',
  leapEvery: 'Every',
  leapPhase: 'Phase',
  leapHint:
    'The leap day lands on the month flagged with a leap length. Every N reveals an interval and a phase.',
  eras: 'Eras',
  erasHint:
    'Ordered, each mapping a canonical start day to a display year. Backward eras count down toward day 0.',
  eraName: 'Era name',
  abbreviation: 'Abbreviation',
  startDay: 'Start day',
  startDayHelp: 'canonical day this era begins',
  yearOne: 'Year one',
  yearOneHelp: "first year's display number",
  direction: 'Direction',
  forward: 'Forward',
  backward: 'Backward',
  addEra: 'Add era',
  weekdayCycle: 'Weekday cycle',
  weekdayHint: 'Ordered labels + a phase picking which weekday lands on day 0. Omit for no-week calendars.',
  phase: 'Phase',
  phaseHelp: 'weekday index sitting at canonical day 0',
  addWeekday: 'Add weekday',
  subDay: 'Sub-day periods',
  subDayHint: 'Named intra-day spans — watches, bells, hours. Used by the session cursor.',
  addPeriod: 'Add period',
  period: 'Period name',
  anchor: 'Anchor',
  anchorHint:
    'Which canonical day the origin sits on, optionally named as an in-world event. This is what day 0 means in the fiction.',
  anchorDay: 'Canonical day',
  anchorEvent: 'Named event',
  livePreview: 'Live preview',
  updatesAsYouEdit: 'updates as you edit',
  readsToPlayers: 'Reads to players as',
  optional: 'optional',
  required: 'required',
  lengthLocked: 'length locked',
  days: 'days',
  errorsBlock: 'Fix errors to save · warnings won’t block',
  noErrors: 'Canonical-day bijection valid · 0 errors',
};

// ---------- options ----------

/** Which surface the editor renders. */
export type CalendarEditorMode = 'list' | 'simple' | 'advanced';

export interface CalendarEditorOptions {
  /** Which surface to render. */
  mode: CalendarEditorMode;

  // ----- list mode -----
  /** Calendars to list (list mode). */
  entries?: readonly CalendarListEntry[];
  /** Open a calendar for edit (campaign-owned) / fork (bundled) / inspect (plugin). */
  onOpenEntry?: (id: string, source: CalendarListSource) => void;
  /** "New calendar" pressed. */
  onNewCalendar?: () => void;
  /** "Browse packs" pressed. */
  onBrowsePacks?: () => void;

  // ----- editor mode -----
  /** The in-progress draft (editor mode). HOST-OWNED. */
  working?: WorkingCalendar;
  /** Pre-computed validation issues (from `validateWorkingCalendar`). */
  issues?: readonly CalendarIssue[];
  /** This draft is a fork of a read-only bundled pack, not yet forked. When
   * `true` the form shows the read-only lock bar + fork prompt instead. */
  forkPending?: boolean;
  /** A localized "forked from {name}" provenance note (editor mode). */
  forkedFrom?: string;

  // edit emitters (host updates its working draft) -------------------------
  onName?: (name: string) => void;
  onMonth?: (index: number, patch: Partial<WorkingMonth>) => void;
  onAddMonth?: () => void;
  onRemoveMonth?: (index: number) => void;
  onToggleIntercalary?: (index: number) => void;
  onLeapRule?: (rule: LeapRule) => void;
  onEra?: (index: number, patch: Partial<WorkingEra>) => void;
  onAddEra?: () => void;
  onRemoveEra?: (index: number) => void;
  onWeekday?: (index: number, name: string) => void;
  onAddWeekday?: () => void;
  onRemoveWeekday?: (index: number) => void;
  onPhase?: (phase: string) => void;
  onPeriod?: (index: number, name: string) => void;
  onAddPeriod?: () => void;
  onRemovePeriod?: (index: number) => void;
  onAnchorDay?: (day: string) => void;
  onAnchorName?: (name: string) => void;

  // chrome actions
  onToggleAdvanced?: () => void;
  onSave?: () => void;
  onCancel?: () => void;
  onFork?: () => void;

  /** Display locale for the preview. Defaults to `en`. */
  locale?: string;
  /** Resolve an issue's `messageKey`+`params` to a string. Defaults to the
   * `messageKey` verbatim (host overrides to thread its `t()`). */
  resolveIssue?: (issue: CalendarIssue) => string;
  /** Optional message-catalog override. */
  messages?: Partial<CalendarEditorMessages>;
  /** Optional `data-testid` prefix to ease E2E. */
  testid?: string;
}

// ---------- the widget ----------

export function calendarEditor(options: CalendarEditorOptions): Mountable {
  const m: CalendarEditorMessages = { ...DEFAULT_CALENDAR_EDITOR_MESSAGES, ...options.messages };
  const locale = options.locale ?? 'en';
  const testid = options.testid;
  const resolveIssue =
    options.resolveIssue ??
    ((issue: CalendarIssue) => issue.messageKey);

  function tid(suffix: string): Record<string, string> {
    return testid !== undefined ? { 'data-testid': `${testid}-${suffix}` } : {};
  }

  /** Render the first matching error issue inline (red, with a ⚠ glyph so
   * colour is never the only signal). The message is resolved via the host's
   * `resolveIssue` (defaults to the raw key). */
  function issueLine(
    issues: readonly CalendarIssue[],
    pred: (f: CalendarIssue['field']) => boolean,
  ): Mountable {
    const issue = issues.find((x) => x.severity === 'error' && pred(x.field));
    return div({ class: 'dl-ce-issue', role: 'alert' }, [
      span({ class: 'dl-ce-issue__glyph', 'aria-hidden': 'true' }, [text('⚠ ')]),
      text(issue !== undefined ? resolveIssue(issue) : ''),
    ]);
  }

  /** A single-line text control. Resolves `testidSuffix` to a full id. */
  function textControl(value: string, onInput: (v: string) => void, o: ControlOpts): Mountable {
    const resolved: ControlOpts =
      o.testidSuffix !== undefined && testid !== undefined
        ? { ...o, testid: `${testid}-${o.testidSuffix}` }
        : o;
    return buildControl('dl-ce-input', false, value, onInput, resolved);
  }

  /** A numeric (string-typed) control. Resolves `testidSuffix` to a full id. */
  function numControl(value: string, onInput: (v: string) => void, o: ControlOpts): Mountable {
    const resolved: ControlOpts =
      o.testidSuffix !== undefined && testid !== undefined
        ? { ...o, testid: `${testid}-${o.testidSuffix}` }
        : o;
    return buildControl('dl-ce-input dl-ce-input--num dl-ce-input--mono', true, value, onInput, resolved);
  }

  if (options.mode === 'list') {
    return viewList();
  }
  return viewEditor(options.mode);

  // ===================== LIST MODE =====================

  function viewList(): Mountable {
    const entries = options.entries ?? [];
    const head = div({ class: 'dl-ce-panel__head' }, [
      div({}, [
        div({ class: 'dl-ce-panel__eyebrow' }, [text(m.listEyebrow)]),
        div({ class: 'dl-ce-panel__title' }, [text(m.listTitle)]),
      ]),
      div({ class: 'dl-ce-head-actions' }, [
        button(
          {
            type: 'button',
            class: 'dl-ce-btn dl-ce-btn--secondary',
            onClick: () => options.onBrowsePacks?.(),
            ...tid('browse-packs'),
          },
          [text(m.browsePacks)],
        ),
        button(
          {
            type: 'button',
            class: 'dl-ce-btn dl-ce-btn--primary',
            onClick: () => options.onNewCalendar?.(),
            ...tid('new-calendar'),
          },
          [span({ class: 'dl-ce-btn__glyph', 'aria-hidden': 'true' }, [text('＋')]), text(m.newCalendar)],
        ),
      ]),
    ]);

    if (entries.length === 0) {
      return div({ class: 'dl-ce-panel', ...(testid !== undefined ? { 'data-testid': testid } : {}) }, [
        head,
        div({ class: 'dl-ce-empty', ...tid('empty') }, [
          div({ class: 'dl-ce-empty__icon', 'aria-hidden': 'true' }, [text('🗓')]),
          div({ class: 'dl-ce-empty__title' }, [text(m.emptyTitle)]),
          div({ class: 'dl-ce-empty__body' }, [text(m.emptyBody)]),
          button(
            {
              type: 'button',
              class: 'dl-ce-btn dl-ce-btn--primary',
              onClick: () => options.onNewCalendar?.(),
              ...tid('empty-new'),
            },
            [text(m.newCalendar)],
          ),
        ]),
      ]);
    }

    return div({ class: 'dl-ce-panel', ...(testid !== undefined ? { 'data-testid': testid } : {}) }, [
      head,
      div(
        { class: 'dl-ce-panel__body' },
        [
          ...entries.map((entry, i) => viewListRow(entry, i)),
          // The single-primary advisory (non-blocking, info tone).
          div({ class: 'dl-ce-advisory', role: 'note', ...tid('primary-advisory') }, [
            span({ class: 'dl-ce-advisory__glyph', 'aria-hidden': 'true' }, [text('ⓘ')]),
            div({}, [
              div({ class: 'dl-ce-advisory__title' }, [text(m.singlePrimaryTitle)]),
              div({ class: 'dl-ce-advisory__body' }, [text(m.singlePrimaryBody)]),
            ]),
          ]),
        ],
      ),
    ]);
  }

  function viewListRow(entry: CalendarListEntry, i: number): Mountable {
    const action =
      entry.source === 'campaign' ? m.edit : entry.source === 'bundled' ? m.fork : m.inspect;
    const badgeText =
      entry.primary === true
        ? m.primary
        : entry.source === 'bundled'
          ? m.bundled
          : entry.source === 'plugin'
            ? m.pluginPreview
            : '';
    const rowChildren: Mountable[] = [
      div({ class: 'dl-ce-list-icon', 'aria-hidden': 'true' }, [text('🗓')]),
      div({ class: 'dl-ce-list-meta' }, [
        div({ class: 'dl-ce-list-name' }, [text(entry.name)]),
        div({ class: 'dl-ce-list-sub' }, [text(entry.summary)]),
      ]),
    ];
    if (entry.edited !== undefined) {
      rowChildren.push(span({ class: 'dl-ce-list-edited' }, [text(entry.edited)]));
    }
    if (badgeText !== '') {
      const tone =
        entry.primary === true ? 'primary' : entry.source === 'bundled' ? 'bundled' : 'plugin';
      rowChildren.push(
        span({ class: `dl-ce-list-badge dl-ce-list-badge--${tone}` }, [
          span({ class: 'dl-ce-list-badge__glyph', 'aria-hidden': 'true' }, [
            text(entry.primary === true ? '★ ' : entry.source === 'bundled' ? '▣ ' : '◔ '),
          ]),
          text(badgeText),
        ]),
      );
    }
    rowChildren.push(
      div({ class: 'dl-ce-list-actions' }, [
        button(
          {
            type: 'button',
            class: 'dl-ce-btn dl-ce-btn--secondary dl-ce-btn--sm',
            onClick: () => options.onOpenEntry?.(entry.id, entry.source),
            ...tid(`open-${i}`),
          },
          [text(action)],
        ),
      ]),
    );
    return div(
      {
        class: `dl-ce-list-row${entry.primary === true ? ' dl-ce-list-row--primary' : ''}`,
        ...tid(`row-${i}`),
      },
      rowChildren,
    );
  }

  // ===================== EDITOR MODE =====================

  function viewEditor(editorMode: 'simple' | 'advanced'): Mountable {
    const working = options.working;
    if (working === undefined) {
      return div({ class: 'dl-ce-panel' }, []);
    }
    const issues = options.issues ?? [];
    const errorCount = issues.filter((x) => x.severity === 'error').length;
    const warnCount = issues.filter((x) => x.severity === 'warning').length;

    const headActions: Mountable[] = [];
    if (editorMode === 'advanced') {
      headActions.push(
        button(
          {
            type: 'button',
            class: 'dl-ce-btn dl-ce-btn--ghost dl-ce-btn--sm',
            onClick: () => options.onToggleAdvanced?.(),
            ...tid('back-to-simple'),
          },
          [text(m.backToSimple)],
        ),
      );
    }

    const head = div({ class: 'dl-ce-panel__head' }, [
      div({}, [
        div({ class: 'dl-ce-panel__eyebrow' }, [
          text(options.forkedFrom !== undefined ? options.forkedFrom : m.identity),
        ]),
        div({ class: 'dl-ce-panel__title' }, [text(working.name === '' ? m.calendarName : working.name)]),
      ]),
      div({ class: 'dl-ce-head-actions' }, headActions),
    ]);

    // Fork-pending: show the read-only lock bar + fork prompt instead of the form.
    const body =
      options.forkPending === true
        ? viewForkPrompt(working)
        : div({ class: 'dl-ce-two' }, [
            div({ class: 'dl-ce-form-rail', ...tid('form') }, viewForm(editorMode, working, issues)),
            div({ class: 'dl-ce-preview-rail' }, [viewPreview(working)]),
          ]);

    const foot = div({ class: 'dl-ce-panel__foot' }, [
      span(
        {
          class: `dl-ce-foot-status${errorCount > 0 ? ' is-error' : ''}`,
          ...tid('foot-status'),
        },
        [
          span({ class: 'dl-ce-foot-status__glyph', 'aria-hidden': 'true' }, [
            text(errorCount > 0 ? '⚠ ' : '✓ '),
          ]),
          text(
            errorCount > 0
              ? `${m.errorsBlock} (${errorCount}${warnCount > 0 ? ` · ${warnCount}` : ''})`
              : m.noErrors,
          ),
        ],
      ),
      span({ class: 'dl-ce-spacer' }),
      button(
        {
          type: 'button',
          class: 'dl-ce-btn dl-ce-btn--ghost',
          onClick: () => options.onCancel?.(),
          ...tid('cancel'),
        },
        [text(m.cancel)],
      ),
      button(
        {
          type: 'button',
          class: 'dl-ce-btn dl-ce-btn--primary',
          ...(errorCount > 0 ? { disabled: true, 'aria-disabled': 'true' } : {}),
          onClick: () => {
            if (errorCount === 0) options.onSave?.();
          },
          ...tid('save'),
        },
        [text(editorMode === 'simple' ? m.done : m.save)],
      ),
    ]);

    return div(
      {
        class: 'dl-ce-panel',
        'data-mode': editorMode,
        ...(testid !== undefined ? { 'data-testid': testid } : {}),
      },
      [head, div({ class: 'dl-ce-panel__body dl-ce-panel__body--editor' }, [body]), foot],
    );
  }

  // ----- the form rail -----

  function viewForm(
    editorMode: 'simple' | 'advanced',
    working: WorkingCalendar,
    issues: readonly CalendarIssue[],
  ): Mountable[] {
    const out: Mountable[] = [];

    // Identity — name (serif, required).
    out.push(
      group(m.identity, '✦', [
        field(m.calendarName, true, [
          textControl(working.name, (v) => options.onName?.(v), {
            serif: true,
            invalid: hasIssue(issues, (f) => f.kind === 'name'),
            testidSuffix: 'name',
            ariaLabel: m.calendarName,
          }),
        ]),
      ]),
    );

    // Months.
    out.push(
      group(
        m.months,
        '▦',
        [
          hint(editorMode === 'simple' ? `${m.renameOnly} · ${working.months.length}` : m.monthsHint),
          viewMonths(editorMode, working, issues),
        ],
        editorMode === 'advanced' ? `${working.months.length}` : undefined,
      ),
    );

    if (editorMode === 'simple') {
      // The progressive-disclosure affordance to the full model.
      out.push(
        button(
          {
            type: 'button',
            class: 'dl-ce-disclose',
            onClick: () => options.onToggleAdvanced?.(),
            ...tid('advanced-toggle'),
          },
          [span({ class: 'dl-ce-disclose__glyph', 'aria-hidden': 'true' }, [text('⚙ ')]), text(m.advanced)],
        ),
      );
      // Era (name + abbrev + starting year) — the rename-path era line.
      const era0 = working.eras[0];
      if (era0 !== undefined) {
        out.push(viewSimpleEra(era0, issues));
      }
      return out;
    }

    // ----- advanced-only sections -----

    // Leap rule.
    out.push(viewLeapRule(working.leapRule));

    // Eras (full).
    out.push(
      group(
        m.eras,
        '◈',
        [
          hint(m.erasHint),
          ...working.eras.map((e, i) => viewEraCard(e, i, issues)),
          addRow(m.addEra, () => options.onAddEra?.(), 'add-era'),
        ],
        `${working.eras.length}`,
      ),
    );

    // Weekday cycle (optional).
    out.push(viewWeekdays(working));

    // Sub-day periods (optional).
    out.push(viewPeriods(working));

    // Anchor.
    out.push(viewAnchor(working, issues));

    return out;
  }

  // ----- months -----

  function viewMonths(
    editorMode: 'simple' | 'advanced',
    working: WorkingCalendar,
    issues: readonly CalendarIssue[],
  ): Mountable {
    const simple = editorMode === 'simple';
    return div({ class: 'dl-ce-months', ...tid('months') }, [
      ...working.months.map((mo, i) => viewMonthRow(mo, i, simple, issues)),
      ...(simple ? [] : [addRow(m.addMonth, () => options.onAddMonth?.(), 'add-month')]),
    ]);
  }

  function viewMonthRow(
    mo: WorkingMonth,
    i: number,
    simple: boolean,
    issues: readonly CalendarIssue[],
  ): Mountable {
    const invalid = hasIssue(issues, (f) => f.kind === 'month' && f.index === i);
    const cells: Mountable[] = [
      span({ class: 'dl-ce-month-idx', 'aria-hidden': 'true' }, [text(String(i + 1))]),
    ];
    // Name input.
    cells.push(
      textControl(mo.name, (v) => options.onMonth?.(i, { name: v }), {
        serif: true,
        variant: 'flush',
        testidSuffix: `month-name-${i}`,
        ariaLabel: `${m.calendarName} ${i + 1}`,
      }),
    );
    if (simple) {
      // Length locked (rename-only path).
      cells.push(
        span({ class: 'dl-ce-month-len dl-ce-month-len--locked' }, [
          span({ class: 'lock', 'aria-hidden': 'true' }, [text('🔒')]),
          span({ class: 'u' }, [text(`${mo.length} ${m.days}`)]),
        ]),
      );
    } else {
      // Length input (+ optional leap length) editable.
      cells.push(
        span({ class: 'dl-ce-month-len' }, [
          numControl(mo.length, (v) => options.onMonth?.(i, { length: v }), {
            invalid,
            testidSuffix: `month-length-${i}`,
            ariaLabel: `${m.days} ${i + 1}`,
          }),
          span({ class: 'u' }, [text(m.days)]),
        ]),
      );
      cells.push(
        span({ class: 'dl-ce-month-actions' }, [
          button(
            {
              type: 'button',
              class: `dl-ce-iconbtn${mo.intercalary === true ? ' is-on' : ''}`,
              'aria-pressed': mo.intercalary === true ? 'true' : 'false',
              'aria-label': m.markIntercalary,
              title: m.markIntercalary,
              onClick: () => options.onToggleIntercalary?.(i),
              ...tid(`month-intercalary-${i}`),
            },
            [text('✦')],
          ),
          button(
            {
              type: 'button',
              class: 'dl-ce-iconbtn',
              'aria-label': m.remove,
              title: m.remove,
              onClick: () => options.onRemoveMonth?.(i),
              ...tid(`month-remove-${i}`),
            },
            [text('🗑')],
          ),
        ]),
      );
    }
    return div(
      {
        class: `dl-ce-month-row${mo.intercalary === true ? ' dl-ce-month-row--intercalary' : ''}${simple ? ' dl-ce-month-row--simple' : ''}`,
        ...tid(`month-row-${i}`),
      },
      [
        ...cells,
        ...(mo.intercalary === true
          ? [span({ class: 'dl-ce-festival-tag' }, [text(m.festival)])]
          : []),
        ...(invalid
          ? [issueLine(issues, (f) => f.kind === 'month' && f.index === i)]
          : []),
      ],
    );
  }

  // ----- leap rule -----

  function viewLeapRule(rule: LeapRule): Mountable {
    const seg = (val: LeapRule['kind'], label: string): Mountable =>
      button(
        {
          type: 'button',
          class: `dl-ce-seg__i${rule.kind === val ? ' is-on' : ''}`,
          'aria-pressed': rule.kind === val ? 'true' : 'false',
          onClick: () => options.onLeapRule?.(leapRuleFor(val, rule)),
          ...tid(`leap-${val}`),
        },
        [text(label)],
      );
    const children: Mountable[] = [
      div({ class: 'dl-ce-seg', role: 'group', 'aria-label': m.leapRule }, [
        seg('none', m.leapNone),
        seg('every_n_years', m.leapEveryN),
        seg('julian', m.leapJulian),
        seg('gregorian', m.leapGregorian),
      ]),
    ];
    if (rule.kind === 'every_n_years') {
      children.push(
        div({ class: 'dl-ce-row dl-ce-row--leap' }, [
          field(m.leapEvery, false, [
            numControl(String(rule.every), (v) => {
              const every = Math.max(1, parseIntOrNull(v) ?? 1);
              options.onLeapRule?.({ kind: 'every_n_years', every, phase: rule.phase });
            }, { testidSuffix: 'leap-every', ariaLabel: m.leapEvery }),
          ]),
          field(m.leapPhase, false, [
            numControl(String(rule.phase), (v) => {
              const phase = parseIntOrNull(v) ?? 0;
              options.onLeapRule?.({ kind: 'every_n_years', every: rule.every, phase });
            }, { testidSuffix: 'leap-phase', ariaLabel: m.leapPhase }),
          ]),
        ]),
      );
    }
    children.push(hint(m.leapHint));
    return group(m.leapRule, '↻', children);
  }

  // ----- eras -----

  function viewSimpleEra(e: WorkingEra, issues: readonly CalendarIssue[]): Mountable {
    return div({ class: 'dl-ce-row dl-ce-row--simple-era' }, [
      field(m.eraName, false, [
        textControl(e.name, (v) => options.onEra?.(0, { name: v }), {
          testidSuffix: 'era-name',
          ariaLabel: m.eraName,
        }),
      ]),
      field(m.abbreviation, false, [
        textControl(e.abbreviation ?? '', (v) => options.onEra?.(0, { abbreviation: v }), {
          mono: true,
          testidSuffix: 'era-abbrev',
          ariaLabel: m.abbreviation,
        }),
      ]),
      field(m.yearOne, false, [
        numControl(e.yearOne, (v) => options.onEra?.(0, { yearOne: v }), {
          invalid: hasIssue(issues, (f) => f.kind === 'era' && f.index === 0),
          testidSuffix: 'era-year-one',
          ariaLabel: m.yearOne,
        }),
      ]),
    ]);
  }

  function viewEraCard(e: WorkingEra, i: number, issues: readonly CalendarIssue[]): Mountable {
    const invalid = hasIssue(issues, (f) => f.kind === 'era' && f.index === i);
    return div(
      { class: `dl-ce-era${invalid ? ' is-invalid' : ''}`, ...tid(`era-${i}`) },
      [
        field(m.eraName, false, [
          textControl(e.name, (v) => options.onEra?.(i, { name: v }), {
            testidSuffix: `era-name-${i}`,
            ariaLabel: m.eraName,
          }),
        ]),
        field(m.abbreviation, false, [
          textControl(e.abbreviation ?? '', (v) => options.onEra?.(i, { abbreviation: v }), {
            mono: true,
            testidSuffix: `era-abbrev-${i}`,
            ariaLabel: m.abbreviation,
          }),
        ]),
        field(m.startDay, false, [
          numControl(e.startDay, (v) => options.onEra?.(i, { startDay: v }), {
            invalid,
            testidSuffix: `era-start-${i}`,
            ariaLabel: m.startDay,
          }),
        ], m.startDayHelp),
        field(m.yearOne, false, [
          numControl(e.yearOne, (v) => options.onEra?.(i, { yearOne: v }), {
            invalid,
            testidSuffix: `era-year-${i}`,
            ariaLabel: m.yearOne,
          }),
        ], m.yearOneHelp),
        div({ class: 'dl-ce-era__full' }, [
          field(m.direction, false, [
            div({ class: 'dl-ce-seg', role: 'group', 'aria-label': m.direction }, [
              dirSeg(e, i, 'forward', m.forward),
              dirSeg(e, i, 'backward', m.backward),
            ]),
          ]),
        ]),
        ...(invalid ? [issueLine(issues, (f) => f.kind === 'era' && f.index === i)] : []),
        ...(i > 0
          ? [
              div({ class: 'dl-ce-era__remove' }, [
                button(
                  {
                    type: 'button',
                    class: 'dl-ce-iconbtn',
                    'aria-label': m.remove,
                    title: m.remove,
                    onClick: () => options.onRemoveEra?.(i),
                    ...tid(`era-remove-${i}`),
                  },
                  [text('🗑')],
                ),
              ]),
            ]
          : []),
      ],
    );
  }

  function dirSeg(e: WorkingEra, i: number, val: EraDirection, label: string): Mountable {
    return button(
      {
        type: 'button',
        class: `dl-ce-seg__i${e.direction === val ? ' is-on' : ''}`,
        'aria-pressed': e.direction === val ? 'true' : 'false',
        onClick: () => options.onEra?.(i, { direction: val }),
        ...tid(`era-dir-${val}-${i}`),
      },
      [span({ 'aria-hidden': 'true' }, [text(val === 'forward' ? '→ ' : '← ')]), text(label)],
    );
  }

  // ----- weekdays -----

  function viewWeekdays(working: WorkingCalendar): Mountable {
    const wd = working.weekdays;
    const chips: Mountable[] = [];
    if (wd !== undefined) {
      wd.labels.forEach((w, i) => {
        chips.push(
          span({ class: 'dl-ce-wd' }, [
            span({ class: 'dl-ce-wd__idx', 'aria-hidden': 'true' }, [text(String(i + 1))]),
            textControl(w.name, (v) => options.onWeekday?.(i, v), {
              serif: true,
              variant: 'flush',
              testidSuffix: `weekday-${i}`,
              ariaLabel: `${m.weekdayCycle} ${i + 1}`,
            }),
            button(
              {
                type: 'button',
                class: 'dl-ce-wd__rm',
                'aria-label': m.remove,
                title: m.remove,
                onClick: () => options.onRemoveWeekday?.(i),
                ...tid(`weekday-remove-${i}`),
              },
              [text('×')],
            ),
          ]),
        );
      });
    }
    chips.push(
      button(
        {
          type: 'button',
          class: 'dl-ce-wd dl-ce-wd--add',
          'aria-label': m.addWeekday,
          title: m.addWeekday,
          onClick: () => options.onAddWeekday?.(),
          ...tid('add-weekday'),
        },
        [text('＋')],
      ),
    );
    const children: Mountable[] = [div({ class: 'dl-ce-wd-row' }, chips)];
    if (wd !== undefined) {
      children.push(
        field(m.phase, false, [
          numControl(wd.phase, (v) => options.onPhase?.(v), {
            testidSuffix: 'weekday-phase',
            ariaLabel: m.phase,
          }),
        ], m.phaseHelp),
      );
    }
    return group(m.weekdayCycle, '▤', [hint(m.weekdayHint), ...children], m.optional);
  }

  // ----- sub-day periods -----

  function viewPeriods(working: WorkingCalendar): Mountable {
    const periods = working.subDayPeriods ?? [];
    return group(
      m.subDay,
      '◷',
      [
        hint(m.subDayHint),
        div({ class: 'dl-ce-months' }, [
          ...periods.map((p, i) =>
            div({ class: 'dl-ce-month-row dl-ce-month-row--simple' }, [
              textControl(p.name, (v) => options.onPeriod?.(i, v), {
                serif: true,
                variant: 'flush',
                testidSuffix: `period-${i}`,
                ariaLabel: `${m.period} ${i + 1}`,
              }),
              span({ class: 'dl-ce-month-actions' }, [
                button(
                  {
                    type: 'button',
                    class: 'dl-ce-iconbtn',
                    'aria-label': m.remove,
                    title: m.remove,
                    onClick: () => options.onRemovePeriod?.(i),
                    ...tid(`period-remove-${i}`),
                  },
                  [text('🗑')],
                ),
              ]),
            ]),
          ),
          addRow(m.addPeriod, () => options.onAddPeriod?.(), 'add-period'),
        ]),
      ],
      `${m.optional}${periods.length > 0 ? ` · ${periods.length}` : ''}`,
    );
  }

  // ----- anchor -----

  function viewAnchor(working: WorkingCalendar, issues: readonly CalendarIssue[]): Mountable {
    const invalid = hasIssue(issues, (f) => f.kind === 'anchor');
    return group(m.anchor, '⚓', [
      hint(m.anchorHint),
      div({ class: 'dl-ce-row dl-ce-row--anchor' }, [
        field(m.anchorDay, false, [
          numControl(working.anchorCanonicalDay, (v) => options.onAnchorDay?.(v), {
            invalid,
            testidSuffix: 'anchor-day',
            ariaLabel: m.anchorDay,
          }),
        ]),
        field(m.anchorEvent, false, [
          textControl(working.anchorName ?? '', (v) => options.onAnchorName?.(v), {
            serif: true,
            testidSuffix: 'anchor-name',
            ariaLabel: m.anchorEvent,
          }),
        ]),
      ]),
      ...(invalid ? [issueLine(issues, (f) => f.kind === 'anchor')] : []),
    ]);
  }

  // ----- fork prompt (read-only bundled pack) -----

  function viewForkPrompt(working: WorkingCalendar): Mountable {
    return div({ class: 'dl-ce-fork-wrap', ...tid('fork-prompt') }, [
      div({ class: 'dl-ce-readonly-bar' }, [
        span({ class: 'glyph', 'aria-hidden': 'true' }, [text('🔒')]),
        text(m.readOnlyBar),
      ]),
      div({ class: 'dl-ce-locked' }, [
        field(m.calendarName, false, [
          textControl(working.name, () => undefined, {
            serif: true,
            disabled: true,
            ariaLabel: m.calendarName,
          }),
        ]),
      ]),
      div({ class: 'dl-ce-fork' }, [
        span({ class: 'glyph', 'aria-hidden': 'true' }, [text('⑂')]),
        div({ class: 'dl-ce-fork__body' }, [
          div({ class: 'dl-ce-fork__title' }, [text(m.forkTitle)]),
          div({ class: 'dl-ce-fork__text' }, [text(m.forkBody)]),
          div({ class: 'dl-ce-fork__action' }, [
            button(
              {
                type: 'button',
                class: 'dl-ce-btn dl-ce-btn--primary dl-ce-btn--sm',
                onClick: () => options.onFork?.(),
                ...tid('fork'),
              },
              [text(m.forkAndEdit)],
            ),
          ]),
        ]),
      ]),
    ]);
  }

  // ===================== LIVE PREVIEW =====================

  function viewPreview(working: WorkingCalendar): Mountable {
    const calendar = previewCalendar(working);
    const tag = div({ class: 'dl-ce-preview__tag' }, [
      span({ class: 'dot', 'aria-hidden': 'true' }),
      text(m.livePreview),
      span({ class: 'live' }, [
        span({ 'aria-hidden': 'true' }, [text('⚡ ')]),
        text(m.updatesAsYouEdit),
      ]),
    ]);
    // The picker reads ANY calendar; feed it a sensible static cursor (the
    // first era / its year_one / the first month). Pure read — no host
    // interaction needed in the preview, it tracks the form via remount.
    const e0 = calendar.eras[0];
    const cursor =
      e0 !== undefined ? { eraId: e0.id, year: e0.year_one, monthIndex: 1 } : undefined;
    return div({ class: 'dl-ce-preview', ...tid('preview') }, [
      tag,
      calendarDatePicker({
        calendar,
        mode: 'inline',
        ...(cursor !== undefined ? { cursor } : {}),
        locale,
        ...(testid !== undefined ? { testid: `${testid}-preview-picker` } : {}),
      }),
    ]);
  }

  // Last-valid-projection fallback: if the working draft can't fold, the
  // preview keeps the last valid calendar so it never blanks. The "last
  // valid" is reconstructed each render from the draft minus the offending
  // field (clamp month lengths to ≥1, drop unparseable eras), so the preview
  // stays representative even mid-error.
  function previewCalendar(working: WorkingCalendar): Calendar {
    const def = workingToDefinition(working);
    if (def !== null) {
      const parsed = CalendarDefinitionSchema.safeParse(def);
      if (parsed.success) return asCalendar(parsed.data);
    }
    // Repair: clamp month lengths, coerce era numbers, default the anchor.
    const repaired = repairWorking(working);
    const rdef = workingToDefinition(repaired);
    if (rdef !== null) {
      const parsed = CalendarDefinitionSchema.safeParse(rdef);
      if (parsed.success) return asCalendar(parsed.data);
    }
    // Final fallback — a minimal one-month calendar so the picker has data.
    return MINIMAL_PREVIEW_CALENDAR;
  }
}

// ---------- shared structural helpers (closure-free) ----------

function leapRuleFor(kind: LeapRule['kind'], prev: LeapRule): LeapRule {
  if (kind === 'every_n_years') {
    if (prev.kind === 'every_n_years') return prev;
    return { kind: 'every_n_years', every: 4, phase: 0 };
  }
  if (kind === 'none') return { kind: 'none' };
  if (kind === 'julian') return { kind: 'julian' };
  return { kind: 'gregorian' };
}

function hasIssue(
  issues: readonly CalendarIssue[],
  pred: (f: CalendarIssue['field']) => boolean,
): boolean {
  return issues.some((x) => x.severity === 'error' && pred(x.field));
}

/** A minimal valid calendar so the preview always has data to render. */
const MINIMAL_PREVIEW_CALENDAR: Calendar = {
  id: 'preview',
  label: 'Preview',
  cycles: { year: { months: [{ name: 'month', length: 30 }] } },
  eras: [{ id: 'e', name: 'Era', start_day: null, year_one: 1, direction: 'forward' }],
  leap_rule: { kind: 'none' },
  anchor_canonical_day: 0,
};

/** Coerce a working draft into something foldable: clamp month lengths to
 * ≥1, default unparseable era numbers, default the anchor. Used only to
 * keep the live preview populated while the GM is mid-edit. */
function repairWorking(working: WorkingCalendar): WorkingCalendar {
  const months: WorkingMonth[] = working.months.map((mo) => {
    const len = mo.length.trim();
    const n = Number(len);
    const valid = len !== '' && Number.isInteger(n) && n > 0;
    return { ...mo, length: valid ? mo.length : '30' };
  });
  const eras: WorkingEra[] = working.eras.map((e) => {
    const yo = Number(e.yearOne.trim());
    const yearOne = Number.isInteger(yo) ? e.yearOne : '1';
    const sdRaw = e.startDay.trim();
    const sd = Number(sdRaw);
    const startDay = sdRaw === '' || Number.isInteger(sd) ? e.startDay : '';
    return { ...e, yearOne, startDay };
  });
  const ad = Number(working.anchorCanonicalDay.trim());
  const anchorCanonicalDay = Number.isInteger(ad) ? working.anchorCanonicalDay : '0';
  return {
    ...working,
    months: months.length > 0 ? months : [{ name: 'month', length: '30' }],
    eras: eras.length > 0 ? eras : [{ id: 'e', name: 'Era', startDay: '', yearOne: '1', direction: 'forward' }],
    anchorCanonicalDay,
  };
}

// ---------- small render helpers (used inside the closure) ----------
//
// These take only their data so they can live at module scope (no `options`
// capture). The interactive ones are defined INSIDE `calendarEditor` above.

function group(
  title: string,
  glyph: string,
  children: readonly Mountable[],
  count?: string,
): Mountable {
  const head: Mountable[] = [
    span({ class: 'dl-ce-group__glyph', 'aria-hidden': 'true' }, [text(glyph)]),
    text(title),
  ];
  if (count !== undefined) head.push(span({ class: 'dl-ce-count' }, [text(count)]));
  return div({ class: 'dl-ce-group' }, [
    div({ class: 'dl-ce-group__h' }, head),
    ...children,
  ]);
}

function field(label: string, required: boolean, children: readonly Mountable[], help?: string): Mountable {
  const labelChildren: Mountable[] = [text(label)];
  if (required) labelChildren.push(span({ class: 'dl-ce-field__req', 'aria-hidden': 'true' }, [text('*')]));
  const out: Mountable[] = [
    span({ class: 'dl-ce-field__label' }, labelChildren),
    ...children,
  ];
  if (help !== undefined) out.push(span({ class: 'dl-ce-field__help' }, [text(help)]));
  return div({ class: 'dl-ce-field' }, out);
}

function hint(textContent: string): Mountable {
  return div({ class: 'dl-ce-group__hint' }, [text(textContent)]);
}

function addRow(label: string, onClick: () => void, testidSuffix: string): Mountable {
  return button(
    {
      type: 'button',
      class: 'dl-ce-addrow',
      onClick,
      'data-testid': `ce-${testidSuffix}`,
    },
    [span({ class: 'dl-ce-addrow__glyph', 'aria-hidden': 'true' }, [text('＋')]), text(label)],
  );
}

interface ControlOpts {
  serif?: boolean;
  mono?: boolean;
  invalid?: boolean;
  disabled?: boolean;
  variant?: 'flush';
  /** Suffix appended to the editor's `testid` prefix by the closure helpers. */
  testidSuffix?: string;
  /** Full, already-prefixed test id (set by the closure). */
  testid?: string;
  ariaLabel?: string;
}

function controlClass(base: string, o: ControlOpts): string {
  let cls = base;
  if (o.serif === true) cls += ' dl-ce-input--serif';
  if (o.mono === true) cls += ' dl-ce-input--mono';
  if (o.variant === 'flush') cls += ' dl-ce-input--flush';
  if (o.invalid === true) cls += ' dl-ce-input--error';
  return cls;
}

/** Build a native input with the editor's chrome. `testid` is the FULL,
 * already-prefixed test id (the closure resolves the suffix via `tid()`). */
function buildControl(
  base: string,
  numeric: boolean,
  value: string,
  onInput: (v: string) => void,
  o: ControlOpts,
): Mountable {
  const props: ElProps = {
    type: 'text',
    ...(numeric ? { inputmode: 'numeric' } : {}),
    class: controlClass(base, o),
    value,
    onInput: (ev: Event) => onInput((ev.target as HTMLInputElement).value),
    ...(o.invalid === true ? { 'aria-invalid': 'true' } : {}),
    ...(o.disabled === true ? { disabled: true } : {}),
    ...(o.ariaLabel !== undefined ? { 'aria-label': o.ariaLabel } : {}),
    ...(o.testid !== undefined ? { 'data-testid': o.testid } : {}),
  };
  return input(props);
}

